<template>
  <div class="page-container">
    <PageHeader title="角色管理">
      <template #actions>
        <div class="role-header-actions">
          <el-input
            v-model="query.roleName"
            placeholder="搜索角色名称"
            clearable
            size="small"
            class="search-input"
            @keyup.enter.native="partserch"
          />
          <el-button type="primary" size="small" icon="el-icon-search" @click="partserch">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增角色</el-button>
        </div>
      </template>
    </PageHeader>

    <TablePanel title="角色列表">
      <template #extra>
        <span class="ai-hint"><i class="el-icon-info"></i>可分配权限</span>
      </template>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :total="total"
        row-id-key="roleId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      />
      <div v-show="!loading && list.length === 0" class="empty-data">暂无角色</div>
    </TablePanel>

    <!-- 新增角色 -->
    <aj-form-add
      :dialog-visible.sync="addDialogVisible"
      :form-config="roleFormConfig"
      :form-data="addFormData"
      dialog-title="新增角色信息"
      dialog-width="45%"
      form-size="small"
      confirm-text="提交新增"
      @confirm="handleAddSubmit"
    />

    <!-- 编辑角色 -->
    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="roleFormConfig"
      :form-data="editFormData"
      dialog-title="编辑角色信息"
      label-width="100px"
      @confirm="handleEditSubmit"
    />

    <!-- 分配权限 -->
    <el-dialog
      v-dialog-drag-resize
      title="分配权限"
      :visible.sync="permDialogVisible"
      width="45%"
      :close-on-click-modal="false"
      @opened="handlePermDialogOpened"
      @closed="handlePermDialogClosed"
    >
      <el-tree
        ref="permTree"
        :data="permTreeData"
        :props="{ children: 'children', label: 'authName' }"
        :default-checked-keys="checkedPermKeys"
        show-checkbox
        node-key="authId"
        default-expand-all
      />
      <span slot="footer">
        <el-button size="small" @click="permDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="permSubmitLoading" @click="handlePermSubmit">确定</el-button>
      </span>
    </el-dialog>

    <!-- 添加用户 -->
    <el-dialog
      v-dialog-drag-resize
      title="编辑角色下的用户"
      :visible.sync="userDialogVisible"
      width="45%"
      :close-on-click-modal="false"
    >
      <el-form :model="userForm" label-width="80px" style="margin-bottom: 16px">
        <el-form-item label="用户">
          <el-select
            v-model="userForm.userIds"
            placeholder="请选择用户"
            multiple
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="u in allUsers"
              :key="u.auId"
              :label="u.auName"
              :value="u.auId"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="已加用户">
          <aj-table
            :data="roleUsers"
            :columns="userTableColumns"
            :use-internal-pagination="false"
            row-id-key="userRoleId"
            stripe
            @operate-btn-click="handleUserOperateBtnClick"
          ></aj-table>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="userDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="userSubmitLoading" @click="handleUserSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getRtRoleList, addRtRole, updateRtRole, deleteRtRole,
  getRtRoleAuthList, updateRtRoleAuth, getRtAuthTree,
  getRtUserList, getRtRoleUserList, updateRtRoleUserMulti, deleteRtRoleUser
} from '@/api/system'
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import PageHeader from '@/components/PageHeader.vue'
import TablePanel from '@/components/TablePanel.vue'

const ROLE_FORM_CONFIG = [
  [
    { type: 'input', label: '角色名称', prop: 'roleName', placeholder: '请输入角色名称', span: 24, required: true }
  ],
  [
    { type: 'input', label: '角色描述', prop: 'roleRemark', placeholder: '请输入角色描述', span: 24 }
  ]
]

const USER_TABLE_COLUMNS = [
  { type: 'index', label: '序号', width: 60 },
  { prop: 'userName', label: '用户名称', minWidth: 120 },
  { prop: 'createtime', label: '添加时间', width: 160 },
  {
    type: 'operation',
    label: '操作',
    width: 80,
    fixed: 'right',
    buttons: [
      { key: 'userDel', text: '删除', type: 'text' }
    ]
  }
]

export default {
  name: 'SystemRolePage',
  components: { AjFormAdd, AjFormEdit, AjTable, PageHeader, TablePanel },
  data () {
    return {
      loading: false,
      submitLoading: false,
      permSubmitLoading: false,
      userSubmitLoading: false,
      list: [],
      total: 0,
      query: { roleName: '', currentPage: 1, pageSize: 10 },
      addDialogVisible: false,
      editDialogVisible: false,
      permDialogVisible: false,
      userDialogVisible: false,
      addFormData: { roleName: '', roleRemark: '' },
      editFormData: { roleId: null, roleName: '', roleRemark: '' },
      permTreeData: [],
      checkedPermKeys: [],
      currentPermRoleId: null,
      authParent: [],
      roleFormConfig: ROLE_FORM_CONFIG,
      // 添加用户
      allUsers: [],
      roleUsers: [],
      currentRoleId: null,
      userForm: { userIds: [] },
      userTableColumns: USER_TABLE_COLUMNS
    }
  },
  computed: {
    columns () {
      return [
        { type: 'index', label: '序号', width: 60, fixed: 'left' },
        { prop: 'roleName', label: '角色名称', minWidth: 140, fixed: 'left' },
        { prop: 'roleRemark', label: '角色描述', minWidth: 180 },
        { prop: 'createusername', label: '创建人', width: 100 },
        { prop: 'createtime', label: '创建时间', width: 160 },
        {
          type: 'operation',
          label: '操作',
          width: 300,
          fixed: 'right',
          buttons: [
            { key: 'edit', text: '编辑', type: 'text' },
            { key: 'delete', text: '删除', type: 'text', class: 'text-danger' },
            { key: 'fpqx', text: '分配权限', type: 'text' },
            { key: 'user', text: '添加用户', type: 'text' }
          ]
        }
      ]
    }
  },
  created () {
    this.fetchList()
    this.fetchPermTree()
    this.fetchAllUsers()
  },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const res = await getRtRoleList(this.query)
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async fetchPermTree () {
      try {
        const res = await getRtAuthTree()
        this.permTreeData = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.permTreeData = []
      }
    },
    async fetchAllUsers () {
      try {
        const res = await getRtUserList({ currentPage: 1, pageSize: 1000 })
        this.allUsers = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.allUsers = []
      }
    },
    partserch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetSearch () {
      this.query.roleName = ''
      this.partserch()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'edit': this.handleEdit(row); break
        case 'delete': this.handleDelete(row); break
        case 'fpqx': this.handlePermission(row); break
        case 'user': this.handleUser(row); break
      }
    },
    handleUserOperateBtnClick ({ btn, row }) {
      if (btn.key === 'userDel') this.handleUserDelete(row)
    },
    handleAdd () {
      this.addFormData = { roleName: '', roleRemark: '' }
      this.addDialogVisible = true
    },
    handleEdit (row) {
      this.editFormData = { roleId: row.roleId, roleName: row.roleName, roleRemark: row.roleRemark || '' }
      this.editDialogVisible = true
    },
    async handleAddSubmit ({ data }) {
      this.submitLoading = true
      try {
        await addRtRole(data)
        this.$message.success('新增成功')
        this.addDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },
    async handleEditSubmit ({ data }) {
      this.submitLoading = true
      try {
        await updateRtRole(data)
        this.$message.success('编辑成功')
        this.editDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该角色吗？', '提示', { type: 'warning' })
        await deleteRtRole({ roleId: row.roleId })
        this.$message.success('删除成功')
        this.fetchList()
      } catch (error) {}
    },
    async handlePermission (row) {
      this.currentPermRoleId = row.roleId
      try {
        const res = await getRtRoleAuthList({ roleId: row.roleId })
        const authList = Array.isArray(res.data) ? res.data : []
        this.checkedPermKeys = this.extractLeafAuthIds(authList)
      } catch (error) {
        this.checkedPermKeys = []
      }
      this.permDialogVisible = true
    },
    handlePermDialogOpened () {
      this.$nextTick(() => {
        if (this.$refs.permTree) {
          this.$refs.permTree.setCheckedKeys(this.checkedPermKeys)
        }
      })
    },
    handlePermDialogClosed () {
      if (this.$refs.permTree) {
        this.$refs.permTree.setCheckedKeys([])
      }
    },
    // 只提取叶子节点ID，避免父节点ID导致全选
    extractLeafAuthIds (list) {
      const ids = []
      const traverse = items => {
        items.forEach(item => {
          if (item.children && item.children.length > 0) {
            traverse(item.children)
          } else {
            ids.push(item.authId)
          }
        })
      }
      traverse(list)
      return ids
    },
    async handlePermSubmit () {
      if (!this.currentPermRoleId) return
      this.permSubmitLoading = true
      try {
        const checkedKeys = this.$refs.permTree.getCheckedKeys()
        const halfCheckedKeys = this.$refs.permTree.getHalfCheckedKeys()
        const allIds = [...checkedKeys, ...halfCheckedKeys]
        await updateRtRoleAuth({ roleId: this.currentPermRoleId, authListString: JSON.stringify(allIds) })
        this.$message.success('权限配置成功')
        this.permDialogVisible = false
      } finally {
        this.permSubmitLoading = false
      }
    },
    async handleUser (row) {
      this.currentRoleId = row.roleId
      this.userForm = { userIds: [] }
      this.roleUsers = []
      this.userDialogVisible = true
      try {
        // 获取角色下已有的用户
        const res = await getRtRoleUserList({ roleId: row.roleId })
        this.roleUsers = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.roleUsers = []
      }
    },
    handleUserDelete (row) {
      this.$confirm('确认移除该用户吗？', '提示', { type: 'warning' })
        .then(async () => {
          try {
            await deleteRtRoleUser({ userRoleId: row.userRoleId })
            this.$message.success('用户已移除')
            // 重新获取角色用户列表
            const res = await getRtRoleUserList({ roleId: this.currentRoleId })
            this.roleUsers = Array.isArray(res.data) ? res.data : []
          } catch (error) {}
        })
        .catch(() => {})
    },
    async handleUserSubmit () {
      if (!this.userForm.userIds || this.userForm.userIds.length === 0) {
        this.$message.warning('请选择用户')
        return
      }
      this.userSubmitLoading = true
      try {
        await updateRtRoleUserMulti({
          roleId: this.currentRoleId,
          userIds: this.userForm.userIds
        })
        this.$message.success('用户配置成功')
        // 重新获取角色用户列表
        const res = await getRtRoleUserList({ roleId: this.currentRoleId })
        this.roleUsers = Array.isArray(res.data) ? res.data : []
        this.userForm.userIds = []
      } finally {
        this.userSubmitLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.role-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .search-input {
    width: 200px;
  }
}

.ai-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  padding: 4px 10px;
  background: #f8fafc;
  border-radius: 20px;
}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px;
  font-size: 14px;
  color: #909399;
}

.text-danger { color: #ef4444 !important; }

::v-deep .aj-table-operation-link {
  margin: 0 4px;
  font-size: 12px;
  font-weight: 500;

  &.text-danger {
    color: #ef4444 !important;
    &:hover { color: #dc2626 !important; }
  }

  &:not(.text-danger):hover { color: #4f7cff !important; }
}
</style>
